import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { LogoDark, LogoWhite } from "../assets";
import { useRoomStore } from "../store/roomStore";
import { FaBars, FaTimes } from "react-icons/fa";

export interface NavItem {
  label: string;
  target?: string;
  href?: string;
}

interface HeaderProps {
  data?: {
    header?: {
      title?: string;
      subtitle?: string;
      logoImage?: string;
      backgroundImage?: string;
      navLinks?: NavItem[];
    };
  };
  homeSection?: string;
  logo?: React.ReactNode;
  siteName?: string;
  onNavClick?: (target: string) => void;
}

export default function Header({
  data,
  homeSection = "home",
  logo,
  siteName,
  onNavClick,
}: HeaderProps) {
  const { templateId } = useParams();
  const basePath = `/templates/${templateId}`;
  const { resetRoomFilterData } = useRoomStore();
  const location = useLocation();
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(homeSection);
  const [isHydrated, setIsHydrated] = useState(false);
  
  const observerRef = useRef<IntersectionObserver | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get navLinks from data
  const navLinks = data?.header?.navLinks || [];
  const headerTitle = data?.header?.title || siteName || "Sosunvilla";
  const logoImage = data?.header?.logoImage;

  // Handle hydration
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Setup intersection observer for active section detection
  useEffect(() => {
    if (!isHydrated || !navLinks.length) return;

    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible?.target.id) {
          setActiveSection(visible.target.id);
        }
      },
      {
        threshold: 0.3,
        rootMargin: "-100px 0px -100px 0px",
      }
    );

    // Observe all sections
    navLinks.forEach((link) => {
      const targetId = link.target || link.href?.replace("#", "") || "";
      if (targetId) {
        const element = document.getElementById(targetId);
        if (element && observerRef.current) {
          observerRef.current.observe(element);
        }
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [navLinks, isHydrated]);

  // Handle hash on load
  useEffect(() => {
    if (!isHydrated) return;

    const hash = window.location.hash.replace("#", "");
    if (hash) {
      const element = document.getElementById(hash);
      if (element) {
        timeoutRef.current = setTimeout(() => {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 150);
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isHydrated]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isMobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  // Scroll to section function
  const scrollToSection = useCallback(
    (sectionId: string) => {
      const element = document.getElementById(sectionId);
      if (!element) return;

      // Close mobile menu if open
      if (isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }

      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      // Update URL hash without causing scroll
      window.history.replaceState(null, "", `#${sectionId}`);

      setActiveSection(sectionId);

      // Call external handler if provided
      if (onNavClick) {
        onNavClick(sectionId);
      }
    },
    [isMobileMenuOpen, onNavClick]
  );

  // Toggle mobile menu
  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
  }, []);

  // Handle logo click
  const handleLogoClick = useCallback(() => {
    resetRoomFilterData();
    setIsMobileMenuOpen(false);
    if (homeSection) {
      scrollToSection(homeSection);
    }
  }, [resetRoomFilterData, homeSection, scrollToSection]);

  // Check if link is active
  const isActiveLink = useCallback(
    (target?: string, href?: string) => {
      const targetId = target || href?.replace("#", "") || "";
      
      // Check for section-based active state
      if (targetId && activeSection === targetId) {
        return true;
      }

      // Check for route-based active state
      if (href === "/") {
        return location.pathname === "/";
      }
      
      if (href) {
        const fullPath = href.startsWith("#") ? href : `${basePath}/${href}`;
        return location.pathname + location.hash === fullPath;
      }
      
      return false;
    },
    [activeSection, basePath, location.pathname, location.hash]
  );

  // Get link style based on state
  const getLinkStyle = useCallback(
    (target?: string, href?: string) => {
      const isActive = isActiveLink(target, href);
      const baseStyle =
        "relative text-[13px] lg:text-[15px] uppercase tracking-[2px] lg:tracking-[3px] font-medium transition-all duration-200";

      if (isActive) {
        return `${baseStyle} text-blue-600 font-semibold`;
      }

      if (isScrolled) {
        return `${baseStyle} text-gray-700 hover:text-blue-500`;
      }

      return `${baseStyle} text-white hover:text-blue-200`;
    },
    [isActiveLink, isScrolled]
  );

  // Get mobile link style
  const getMobileLinkStyle = useCallback(
    (target?: string, href?: string) => {
      const isActive = isActiveLink(target, href);
      return `text-left py-3 uppercase tracking-[2px] border-b border-gray-100 transition-colors ${
        isActive
          ? "text-blue-600 border-blue-600 font-semibold"
          : "text-gray-700 hover:text-blue-600"
      }`;
    },
    [isActiveLink]
  );

  // Get active indicator
  const getActiveIndicator = useCallback(
    (target?: string, href?: string) => {
      if (isActiveLink(target, href)) {
        return (
          <span className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full bg-blue-600" />
        );
      }
      return null;
    },
    [isActiveLink]
  );

  // Render navigation link
  const renderNavLink = useCallback(
    (item: NavItem, isMobile = false) => {
      const { label, target, href } = item;
      const targetId = target || href?.replace("#", "") || "";

      // If it's a hash link (section-based navigation)
      if (target || (href && href.startsWith("#"))) {
        return (
          <button
            key={label}
            type="button"
            onClick={() => scrollToSection(targetId)}
            className={isMobile ? getMobileLinkStyle(target, href) : getLinkStyle(target, href)}
          >
            {label}
            {!isMobile && getActiveIndicator(target, href)}
          </button>
        );
      }

      // If it's a route link
      if (href) {
        const fullPath = href.startsWith("/") ? href : `${basePath}/${href}`;
        return (
          <Link
            key={label}
            to={fullPath}
            className={isMobile ? getMobileLinkStyle(target, href) : getLinkStyle(target, href)}
          >
            {label}
            {!isMobile && getActiveIndicator(target, href)}
          </Link>
        );
      }

      // Fallback: treat as section
      return (
        <button
          key={label}
          type="button"
          onClick={() => scrollToSection(targetId || label.toLowerCase())}
          className={isMobile ? getMobileLinkStyle(target, href) : getLinkStyle(target, href)}
        >
          {label}
          {!isMobile && getActiveIndicator(target, href)}
        </button>
      );
    },
    [basePath, scrollToSection, getLinkStyle, getMobileLinkStyle, getActiveIndicator]
  );

  // Determine logo to display
  const getLogo = () => {
    if (logo) return logo;
    if (logoImage) {
      return (
        <img
          src={logoImage}
          alt={headerTitle}
          className="h-auto w-[140px] sm:w-[160px] object-contain"
        />
      );
    }
    // Fallback to default logo
    return (
      <img
        src={isScrolled ? LogoDark : LogoWhite}
        alt="Logo"
        className="h-auto w-[140px] sm:w-[160px]"
      />
    );
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-lg py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6">
        {/* Logo */}
        <button
          type="button"
          onClick={handleLogoClick}
          className={`flex items-center gap-2 shrink-0 transition-transform duration-200 hover:scale-105 ${
            isScrolled ? "text-indigo-800 font-extrabold" : "text-amber-100"
          }`}
          aria-label="Go to Home"
        >
          {getLogo()}
          {headerTitle && !logo && !logoImage && (
            <span className="font-primary text-lg font-bold hidden sm:inline">
              {headerTitle}
            </span>
          )}
        </button>

        {/* Desktop Navigation */}
        {navLinks.length > 0 && (
          <nav className="hidden md:flex items-center gap-8 lg:gap-10">
            {navLinks.map((item) => renderNavLink(item, false))}
          </nav>
        )}

        {/* Mobile Menu Button */}
        <button
          type="button"
          onClick={toggleMobileMenu}
          className="md:hidden text-2xl p-2 rounded-lg hover:bg-white/10 transition-colors"
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? (
            <FaTimes className={isScrolled ? "text-gray-800" : "text-white"} />
          ) : (
            <FaBars className={isScrolled ? "text-gray-800" : "text-white"} />
          )}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`md:hidden fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          isMobileMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile Menu */}
      {navLinks.length > 0 && (
        <div
          className={`md:hidden fixed left-0 right-0 top-[72px] bg-white shadow-2xl transition-all duration-300 transform ${
            isMobileMenuOpen
              ? "opacity-100 translate-y-0 pointer-events-auto"
              : "opacity-0 -translate-y-4 pointer-events-none"
          }`}
        >
          <nav className="flex flex-col p-6 space-y-1 max-h-[calc(100vh-72px)] overflow-y-auto">
            {navLinks.map((item) => renderNavLink(item, true))}
            
            {/* Optional: Add extra mobile footer items */}
            <div className="pt-4 mt-4 border-t border-gray-200">
              <div className="text-xs text-gray-400 text-center">
                {headerTitle || "Navigate"}
              </div>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}